import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { AddToWishlistButton, AddToCartButton } from "./ButtonComponents";
import { RentNowButton } from "./RentNowButton";
import RatingStars from "./RatingStars";
import config from "../../../config";
import ImageWithPlaceholder from "../../common/ImageWithPlaceholder";

export default function BookList({ book }) {
  const { image, title, author, isbn, description, rating } = book;
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  return (
    <motion.div
      className="group relative mb-4 w-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ type: "spring", stiffness: 120 }}
    >
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="relative bg-white/50 backdrop-blur-md rounded-2xl shadow-inner hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 p-3 sm:p-4 md:p-6"
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-transparent to-primary/30 opacity-0 group-hover:opacity-20 transition-opacity duration-500 pointer-events-none" />

        {/* Wishlist Button */}
        <div className="absolute top-2 right-2 z-10">
          <AddToWishlistButton book={book} />
        </div>

        {/* Responsive Layout: column on mobile, row on larger */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Book Image */}
          <div className="flex justify-center sm:justify-start items-center">
            <motion.div
              className="w-28 h-40 sm:w-10 md:w-32 sm:h-auto flex-shrink-0 z-10"
              whileHover={{ scale: hovered ? 1.03 : 1 }}
              onClick={() => navigate(`/book/${book.id}`)}
            >
              <ImageWithPlaceholder
                src={
                  image && image !== "defaultImage.jpg"
                    ? `${config.url}${image}`
                    : "/images/defaultBook.png"
                }
                alt={title}
                className="w-full h-full object-cover rounded-md shadow-lg cursor-pointer"
                fallbackSrc="/images/defaultBook.png"
              />
            </motion.div>
          </div>

          {/* Book Details */}
          <div className="flex flex-col justify-between flex-1 z-10 mt-2 sm:mt-0">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                {title}
              </h3>
              <p className="mt-1 text-gray-700 text-sm leading-relaxed line-clamp-3">
                {description}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-600">
                <RatingStars rating={rating} size={14} />
                <span className="h-1 w-1 bg-gray-400 rounded-full" />
                <span>By {author}</span>
                <span className="h-1 w-1 bg-gray-400 rounded-full" />
                <span>ISBN: {isbn}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-3 flex flex-wrap gap-2">
              <div className="w-32 sm:w-40">
                <motion.div whileHover={{ scale: 1.05 }}>
                  <RentNowButton book={book} />
                </motion.div>
              </div>

              <div className="w-32 sm:w-40">
                <motion.div whileHover={{ scale: 1.05 }}>
                  <AddToCartButton book={book} />
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
